import { T } from '../data/tokens';
import FlatIcon from './FlatIcon';
import Gallery from './Gallery';
import Accordion from './Accordion';

const accent  = '#E60012';
const accentR = '230,0,18';
  
const SCREENS = [
  { label: 'Dashboard Operations Overview',  src: 'images/dashboard-operations-overview.png',  bg: '#FFF1F2', fg: '#be123c' },
  { label: 'Mission- Setup Tower Selection',      src: 'images/mission-setup-tower-selection.png',      bg: '#FEF2F2', fg: '#dc2626' },
  { label: 'Tower Selection Map View',  src: 'images/mission-setup-tower-selection-mapview.png',  bg: '#FFF7ED', fg: '#c2410c' },
  { label: 'Pre Flight Checklist',  src: 'images/pre-flight-checklist.png',  bg: '#F0FDF4', fg: '#15803d' },
  { label: 'Live Inspection Field Mode',  src: 'images/live-inspection-field-mode.png',  bg: '#F0FDF4', fg: '#15803d' },
  { label: 'Anomaly Review',  src: 'images/anomaly-review.png',  bg: '#F0FDF4', fg: '#15803d' },
];   

const IMPACT_GROUPS = [
  { groupLabel: 'Operational Efficiency', groupIcon: 'bolt', stats: [
    { num: '75%',  desc: 'Faster Inspection Cycles',  sub: 'vs. traditional climbing inspection' },
    { num: '8min', desc: 'Pre-Mission Setup',          sub: 'Down from 45 minutes' },
    { num: '6x',   desc: 'More Towers Per Day',        sub: 'Per engineer per shift' },
  ]},
  { groupLabel: 'Cost & Safety', groupIcon: 'monetization', stats: [
    { num: '85%',  desc: 'Cost Reduction',             sub: 'vs. helicopter-based surveys' },
    { num: '0',    desc: 'Missed Critical Anomalies',  sub: 'During 3-month pilot' },
    { num: '100%', desc: 'Climb-Free Inspection',      sub: 'Eliminated high-risk manual access' },
  ]},
  { groupLabel: 'AI Adoption', groupIcon: 'smart_toy', stats: [
    { num: '92%',  desc: 'Engineer Trust Score',       sub: 'For AI recommendations' },
    { num: '4hrs', desc: 'Paperwork Eliminated',       sub: 'Per day per engineer' },
    { num: '78',   desc: 'SUS Score',                  sub: 'Up from 41 (legacy system)' },
  ]},
];

export default function CaseStudyDrone() {
  return (
    <div>

      {/* ── Hero ────────────────────────────────────────────────────── */}
      <div style={{ background: 'linear-gradient(135deg, #0d1b2a 0%, #1a3a4a 50%, #0a2a3a 100%)', color: 'white', padding: '3.5rem 3rem 3rem', position: 'relative', overflow: 'hidden' }} className="cs-hero">
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `linear-gradient(rgba(${accentR},0.15) 1px,transparent 1px),linear-gradient(90deg,rgba(${accentR},0.15) 1px,transparent 1px)`, backgroundSize: '48px 48px', pointerEvents: 'none' }} />
        <div style={T.heroInner}>
          <span style={{ ...T.pill, background: `rgba(${accentR},0.2)`, border: `1px solid rgba(${accentR},0.4)`, color: '#ffb3b8' }}>Industrial UX · AI Interface · Field Technology</span>
          <h2 style={T.heroTitle}>Drone-Based Tower Health Inspection</h2>
          <p style={T.heroSub}>HIBI — AI-Powered Infrastructure Monitoring</p>
          <p style={{ ...T.heroTagline, color: accent }}>75% Faster Inspections &amp; 85% Cost Savings vs. Helicopter Surveys</p>
          <div style={{ ...T.resultBand, borderLeft: '4px solid #E60012' }}>
            <p style={T.resultText}>
              I designed the end-to-end UX for an <strong>AI-powered drone inspection platform</strong> used by field engineers in high-voltage tower environments. The interface had to work on tablets in direct sunlight, support gloved hands, and surface complex AI anomaly detection in plain language that non-specialist engineers could trust and act on immediately.
            </p>
          </div>
          <div style={T.metricsRow} className="cs-metrics">
            <div style={T.metric}><span style={T.metricNum}>75%</span><span style={T.metricLabel}>Faster Inspections</span><span style={T.metricSub}>vs. traditional methods</span></div>
            <div style={T.metric}><span style={T.metricNum}>85%</span><span style={T.metricLabel}>Cost Reduction</span><span style={T.metricSub}>vs. helicopter surveys</span></div>
            <div style={T.metric}><span style={T.metricNum}>0</span><span style={T.metricLabel}>Missed Anomalies</span><span style={T.metricSub}>In pilot testing</span></div>
            <div style={T.metric}><span style={T.metricNum}>92%</span><span style={T.metricLabel}>Engineer Trust Score</span><span style={T.metricSub}>AI recommendations</span></div>
          </div>
        </div>
      </div>

      {/* ── Stats Strip ─────────────────────────────────────────────── */}
      <div style={T.detailStatsStrip} className="detail-stats-strip">
        <div style={{ ...T.detailStatCell, borderRight: '1px solid rgba(0,0,0,0.07)' }} className="detail-stat-cell"><span style={T.detailStatCellLabel}>Role</span><span style={T.detailStatCellValue}>Senior UX / UI Consultant / Product design Lead</span></div>
        <div style={{ ...T.detailStatCell, borderRight: '1px solid rgba(0,0,0,0.07)' }} className="detail-stat-cell"><span style={T.detailStatCellLabel}>Duration</span><span style={T.detailStatCellValue}>12 Months</span></div>
        <div style={{ ...T.detailStatCell }} className="detail-stat-cell"><span style={T.detailStatCellLabel}>Team</span><span style={T.detailStatCellValue}>12-Person Product Team</span></div>
      </div>

      {/* ── Problem ─────────────────────────────────────────────────── */}
      <div style={T.section} className="cs-section">
        <p style={{ ...T.sectionLabel, color: accent }}><FlatIcon name="help" size={14} color={accent} style={{marginRight:"6px",verticalAlign:"middle"}} />The Problem</p>
        <h3 style={T.sectionTitle}>Dangerous, expensive and slow tower inspections</h3>
        <p style={T.sectionIntro}>Tower health inspections required engineers to climb 100m structures or hire helicopters at enormous cost and risk. AI anomaly detection existed but field engineers couldn't trust outputs they didn't understand. Standard UX principles failed — the interface had to work in bright sunlight, with gloved hands, under time pressure, in environments with no room for error.</p>
        <div style={{ display: 'flex', gap: '12px', marginTop: '1.25rem' }}>
          {[
            { v: '45 min', l: 'per manual inspection' },
            { v: '100m+',  l: 'tower climb height' },
            { v: '0%',     l: 'AI trust pre-redesign' },
          ].map((s) => (
            <div key={s.v} style={{ flex: 1, borderRadius: '10px', padding: '1rem 1.25rem', background: `rgba(${accentR},0.06)`, border: `1px solid rgba(${accentR},0.2)` }}>
              <div style={{ fontFamily: "'DM Serif Display',Georgia,serif", fontSize: '1.7rem', color: accent, lineHeight: 1, marginBottom: '4px' }}>{s.v}</div>
              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.65rem', color: '#64748b', letterSpacing: '0.04em' }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Solution — 5 cards ──────────────────────────────────────── */}
      <div style={T.section} className="cs-section">
        <p style={{ ...T.sectionLabel, color: accent }}><FlatIcon name="rocket" size={14} color={accent} style={{marginRight:"6px",verticalAlign:"middle"}} />The Final Solution</p>
        <h3 style={T.sectionTitle}>What We Built</h3>
        <p style={T.sectionIntro}>A tablet-first inspection platform that guides field engineers through drone-assisted tower surveys, surfaces AI anomaly detection in plain language, and generates compliance reports automatically — all designed to work in the harshest outdoor conditions.</p>
        <div style={T.solCards}>
          <div style={T.solCard}>
            <div style={{ ...T.solNum, color: accent }}>01</div>
            <div style={{ flex: 1 }}>
              <h4 style={T.solTitle}>High-Contrast Field Interface <span style={{ ...T.solTag, background: `rgba(${accentR},0.1)`, color: accent }}>Core Design Challenge</span></h4>
              <p style={T.solBody}>A purpose-built tablet UI with oversized touch targets (minimum 48px), maximum contrast ratios, and glare-resistant colour palette — operable with insulated gloves in direct sunlight at 50m heights.</p>
              <div style={{ ...T.solResult, color: accent, background: `rgba(${accentR},0.08)`, border: `1px solid rgba(${accentR},0.2)` }}>Result: Zero input errors reported during 3-month field pilot</div>
            </div>
          </div>
          <div style={T.solCard}>
            <div style={{ ...T.solNum, color: accent }}>02</div>
            <div style={{ flex: 1 }}>
              <h4 style={T.solTitle}>AI Anomaly Detection Dashboard</h4>
              <p style={T.solBody}>Translates raw AI model outputs into plain-language severity alerts with visual overlays on drone imagery, enabling non-specialist engineers to identify and classify defects without deep AI knowledge.</p>
            </div>
          </div>
          <div style={T.solCard}>
            <div style={{ ...T.solNum, color: accent }}>03</div>
            <div style={{ flex: 1 }}>
              <h4 style={T.solTitle}>Guided Inspection Workflow</h4>
              <p style={T.solBody}>Step-by-step mission planner that auto-generates drone flight paths based on tower type and inspection scope, reducing pre-mission setup from 45 minutes to under 8 minutes.</p>
            </div>
          </div>
          <div style={T.solCard}>
            <div style={{ ...T.solNum, color: accent }}>04</div>
            <div style={{ flex: 1 }}>
              <h4 style={T.solTitle}>Offline-First Data Capture</h4>
              <p style={T.solBody}>Full functionality in zero-connectivity environments — all inspection data, annotations, and AI results sync automatically when the engineer returns to connectivity range.</p>
            </div>
          </div>
          <div style={T.solCard}>
            <div style={{ ...T.solNum, color: accent }}>05</div>
            <div style={{ flex: 1 }}>
              <h4 style={T.solTitle}>Automated Compliance Reporting</h4>
              <p style={T.solBody}>One-tap generation of regulatory-compliant inspection reports with drone imagery, AI findings, engineer annotations, and GPS coordinates — eliminating 4 hours of post-inspection paperwork.</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Gallery ─────────────────────────────────────────────────── */}
      <Gallery screens={SCREENS} accent={accent} />

      {/* ── Design Strategy ─────────────────────────────────────────── */}
      <Accordion label="🎯 The Design Strategy" accent={accent}>
        <div style={{ ...T.section, background: '#0a0e1a' }} className="cs-section">
          <h3 style={{ ...T.sectionTitle, color: '#fff' }}>Designing for Extreme Conditions</h3>
          <p style={{ ...T.sectionIntro, color: 'rgba(255,255,255,0.8)' }}>Standard UX principles do not apply when your user is 50 metres in the air wearing insulated gloves, fighting wind, and making safety-critical decisions under time pressure.</p>
          <div style={T.strategyGrid} className="cs-strategy-grid">
            <div style={T.strategyBlock}>
              <h4 style={{ ...T.strategyHeading, color: accent }}>The Design Pivot</h4>
              <p style={{ ...T.sectionIntro, color: 'rgba(255,255,255,0.75)', marginTop: '0.5rem', marginBottom: 0 }}>
                Initial designs followed standard enterprise UI patterns — dense data tables, small interactive elements, complex navigation. Field testing revealed these were completely unusable at height. The pivot: <strong>Extreme Simplification</strong> — every screen reduced to a single primary action, with secondary actions hidden until needed.
              </p>
            </div>
            <div style={T.strategyBlock}>
              <h4 style={{ ...T.strategyHeading, color: accent }}>The Critical Trade-off</h4>
              <div style={T.tradeoffItem}>
                <span style={{ ...T.tradeoffLabel, color: accent }}>The Tension</span>
                <span style={T.tradeoffText}>Office-based managers wanted rich data and full control. Field engineers needed extreme simplicity and speed.</span>
              </div>
              <div style={{ ...T.tradeoffItem, marginTop: '0.9rem' }}>
                <span style={{ ...T.tradeoffLabel, color: accent }}>The Decision</span>
                <span style={T.tradeoffText}>Two separate interface modes — a simplified Field Mode for engineers at height, and a full Desktop Mode for managers reviewing results in the office.</span>
              </div>
            </div>
          </div>
        </div>
      </Accordion>

      {/* ── Journey ─────────────────────────────────────────────────── */}
      <Accordion label="🔍 The Journey" accent={accent}>
        <div style={T.section} className="cs-section">
          <h3 style={T.sectionTitle}>How We Got There</h3>
          <div style={T.journeyGrid} className="cs-journey-grid">

            {/* Challenge card */}
            <div style={T.journeyCard}>
              <div style={T.journeyIcon}><FlatIcon name="warning" size={24} color="currentColor" /></div>
              <h4 style={T.journeyTitle}>The Challenge &amp; Pain Points</h4>
              <p style={T.sectionIntro}>High-voltage tower inspections were conducted manually by climbing engineers or expensive helicopter surveys — both slow, dangerous, and costly. Existing drone tools were designed for pilots, not infrastructure engineers.</p>
              {[
                { q: '"I cannot read the screen up here. The sun makes everything invisible and I cannot use the touchscreen with my safety gloves on."', who: '— Field Engineer, Site Visit' },
                { q: '"The AI flags 200 things per tower. I need to know which three actually matter today. I do not have time to interpret machine learning outputs."', who: '— Senior Infrastructure Engineer' },
                { q: '"Writing the post-inspection report takes longer than the inspection itself. And I have to do six towers a day."', who: '— Field Inspection Team Lead' },
              ].map((q) => (
                <div key={q.who} style={{ ...T.quote, borderLeft: '3px solid #E60012' }}>
                  <p style={T.quoteText}>{q.q}</p>
                  <cite style={{ ...T.quoteCite, color: accent }}>{q.who}</cite>
                </div>
              ))}
            </div>

            {/* Aha Moment card */}
            <div style={{ ...T.journeyCard, background: 'rgba(230,0,18,0.04)', border: '1px solid rgba(230,0,18,0.15)' }}>
              <div style={T.journeyIcon}><FlatIcon name="lightbulb" size={24} color="currentColor" /></div>
              <h4 style={T.journeyTitle}>The Aha Moment</h4>
              <p style={T.sectionIntro}>During a site visit, I watched a field engineer squinting at a standard tablet UI in bright sunlight, jabbing at small buttons with gloved fingers and missing every tap. The problem was not the data or the AI — it was that <strong>no one had ever designed for this physical environment.</strong></p>
              <p style={{ ...T.sectionIntro, marginTop: '0.75rem' }}>
                Every design decision from that point was filtered through one question: <strong>Can an engineer do this with one gloved hand, in direct sunlight, at height, under time pressure?</strong> If not, redesign it.
              </p>
            </div>
          </div>

          {/* Research Methods */}
          <h4 style={T.subsectionTitle}>Research Methods</h4>
          <div style={T.researchGrid} className="cs-research-grid">
            {[
              { icon: 'construction', title: 'Site Visits & Contextual Inquiry', desc: 'Accompanied 8 field engineers on live tower inspections across 3 sites' },
              { icon: 'mic', title: 'Stakeholder Interviews',           desc: '22 interviews with engineers, safety officers, and operations managers' },
              { icon: 'science', title: 'Simulated Field Testing',          desc: 'Usability tests conducted outdoors in sunlight with safety gloves and time pressure' },
              { icon: 'clipboard', title: 'Workflow Shadowing',               desc: 'Mapped the full inspection process from pre-mission planning to report submission' },
            ].map((r) => (
              <div key={r.title} style={T.researchItem}>
                <FlatIcon name={r.icon} size={20} color="currentColor" style={T.researchIcon} />
                <div>
                  <strong style={T.researchTitle}>{r.title}</strong>
                  <p style={T.researchDesc}>{r.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Key Insights */}
          <h4 style={T.subsectionTitle}>Key Insights</h4>
          <div style={T.researchGrid} className="cs-research-grid">
            {[
              { icon: 'wb_sunny', title: 'Environment is the UX',         desc: 'Sunlight, wind, height, and gloves fundamentally change how users interact with any interface. Standard WCAG contrast ratios are not enough outdoors.' },
              { icon: 'smart_toy', title: 'AI Trust Requires Explainability', desc: 'Engineers would not act on AI recommendations they could not understand. Plain-language explanations were more important than accuracy percentages.' },
              { icon: 'error', title: 'Safety Overrides Efficiency',    desc: 'Any feature that increased cognitive load at height was a safety risk. Speed of task completion mattered less than reduction of errors.' },
              { icon: 'wifi_off', title: 'Offline is the Default',         desc: 'Remote tower locations had zero reliable connectivity. Offline-first was not a nice-to-have — it was a fundamental requirement for any adoption.' },
            ].map((r) => (
              <div key={r.title} style={T.researchItem}>
                <FlatIcon name={r.icon} size={20} color="currentColor" style={T.researchIcon} />
                <div>
                  <strong style={T.researchTitle}>{r.title}</strong>
                  <p style={T.researchDesc}>{r.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Testing & Iteration */}
          <h4 style={T.subsectionTitle}>Testing &amp; Iteration</h4>
          <div style={T.iterTimeline} className="cs-iter-timeline">
            <div style={{ ...T.iterItem, background: 'rgba(239,68,68,0.06)', borderColor: 'rgba(239,68,68,0.2)' }}>
              <span style={{ ...T.iterDot, background: '#ef4444' }} />
              <div>
                <strong style={T.iterLabel}>Round 1</strong>
                <p style={T.iterText}>Standard enterprise UI — <span style={{ color: '#ef4444', fontWeight: 600 }}>failed all outdoor tests</span></p>
              </div>
            </div>
            <span style={T.iterArrow}>→</span>
            <div style={{ ...T.iterItem, background: 'rgba(251,146,60,0.06)', borderColor: 'rgba(251,146,60,0.2)' }}>
              <span style={{ ...T.iterDot, background: '#f97316' }} />
              <div>
                <strong style={T.iterLabel}>Round 2</strong>
                <p style={T.iterText}>High-contrast redesign — <span style={{ color: '#f97316', fontWeight: 600 }}>touch targets still too small</span></p>
              </div>
            </div>
            <span style={T.iterArrow}>→</span>
            <div style={{ ...T.iterItem, background: 'rgba(230,0,18,0.07)', borderColor: 'rgba(230,0,18,0.25)' }}>
              <span style={{ ...T.iterDot, background: '#E60012' }} />
              <div>
                <strong style={T.iterLabel}>Round 3 (Final)</strong>
                <p style={T.iterText}>Full Field Mode with glove-optimised targets — <span style={{ color: '#E60012', fontWeight: 600 }}>zero input errors in pilot</span></p>
              </div>
            </div>
          </div>
        </div>
      </Accordion>

      {/* ── Design Process ──────────────────────────────────────────── */}
      <Accordion label="✏️ Design Process" accent={accent}>
        <div style={{ ...T.section, background: '#fafaf8' }} className="cs-section">
          <h3 style={T.sectionTitle}>The Messy Middle</h3>
          <p style={T.sectionIntro}>Before zero input errors, there were three rounds of failures. The standard UX playbook did not survive its first contact with a field engineer at 50 metres.</p>

          {/* What I Believed — red callout box */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderLeft: `4px solid #E60012`, borderRadius: '10px', padding: '1.5rem 1.75rem', marginBottom: '1.5rem', marginTop: '1.25rem' }}>
            <p style={{ fontSize: '0.72rem', fontFamily: "'DM Mono', monospace", color: '#E60012', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.5rem', fontWeight: 600 }}>What I Believed at the Start</p>
            <p style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1A1A1A', fontFamily: "'DM Sans', sans-serif", lineHeight: 1.45, marginBottom: '0.75rem' }}>
              "Good UX principles are universal. High contrast and clear labels will be enough for outdoor use."
            </p>
            <p style={{ fontSize: '0.95rem', color: '#64748b', lineHeight: 1.8, fontFamily: "'DM Sans', sans-serif", margin: 0 }}>
              I designed the first version at my desk using standard accessibility guidelines. WCAG AA contrast ratios. Clear icons. Logical navigation. In a lab setting it tested well. In a field test at noon in direct sunlight with safety gloves, it was completely unusable.
            </p>
          </div>

          {/* 3 Interface Approaches — 3-col grid */}
          <h4 style={T.subsectionTitle}>3 Interface Approaches — Only One Survived the Field</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '2rem' }} className="cs-three-col">
            {[
              { dot: '#ef4444', border: '#fecaca', label: 'ROUND 1 — FAILED',  title: 'Standard Enterprise UI',   body: 'WCAG AA compliant, standard touch targets, clean layout. Failed every outdoor test. Engineers could not read the screen and missed every tap with gloves on.' },
              { dot: '#f97316', border: '#fed7aa', label: 'ROUND 2 — PARTIAL', title: 'High-Contrast Redesign',    body: 'Pushed contrast far beyond WCAG minimums, increased font sizes. Screen was readable — but touch targets were still too small for insulated gloves. Error rate remained high.' },
              { dot: '#22c55e', border: '#bbf7d0', label: 'ROUND 3 — SHIPPED', title: 'Full Field Mode',            body: 'Minimum 48px touch targets, single primary action per screen, glove-optimised spacing. Zero input errors in the 3-month pilot.' },
            ].map((v) => (
              <div key={v.label} style={{ background: '#fff', border: `1px solid ${v.border}`, borderRadius: '10px', padding: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: v.dot, display: 'inline-block' }} />
                  <strong style={{ fontSize: '0.78rem', color: v.dot, fontFamily: "'DM Mono', monospace", letterSpacing: '0.04em' }}>{v.label}</strong>
                </div>
                <p style={{ fontSize: '0.92rem', fontWeight: 700, color: '#1A1A1A', fontFamily: "'DM Sans', sans-serif", marginBottom: '0.5rem' }}>{v.title}</p>
                <p style={{ fontSize: '0.88rem', color: '#64748b', lineHeight: 1.65, fontFamily: "'DM Sans', sans-serif", margin: 0 }}>{v.body}</p>
              </div>
            ))}
          </div>

          {/* How the Anomaly Severity Display Evolved — 4-col grid */}
          <h4 style={T.subsectionTitle}>How the Anomaly Severity Display Evolved</h4>
          <p style={{ ...T.sectionIntro, marginBottom: '1.25rem' }}>Engineers refused to act on outputs they could not understand. It took 4 iterations to find the right language.</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.75rem', marginBottom: '2rem' }} className="cs-four-col">
            {[
              { v: 'V1', label: 'Raw confidence scores',          result: 'Not trusted',  color: '#ef4444', note: '87.3% confidence meant nothing to a non-specialist engineer' },
              { v: 'V2', label: 'Traffic light system',           result: 'Ambiguous',    color: '#f97316', note: 'Red/amber/green without context — engineers did not know what action to take' },
              { v: 'V3', label: 'Severity + action prompt',       result: 'Better',       color: '#eab308', note: 'Told engineers what was wrong but not how urgent. Decisions still delayed.' },
              { v: 'V4', label: 'Plain-language + urgency + action', result: '92% trust', color: '#22c55e', note: 'Critical: Replace within 30 days. Engineers acted immediately without consulting anyone.' },
            ].map((e) => (
              <div key={e.v} style={{ background: '#fff', border: `1px solid ${e.color}44`, borderTop: `3px solid ${e.color}`, borderRadius: '8px', padding: '1rem' }}>
                <div style={{ fontSize: '0.68rem', fontFamily: "'DM Mono', monospace", color: e.color, fontWeight: 700, marginBottom: '0.4rem', letterSpacing: '0.06em' }}>{e.v}</div>
                <p style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1A1A1A', fontFamily: "'DM Sans', sans-serif", marginBottom: '0.35rem', lineHeight: 1.35 }}>{e.label}</p>
                <p style={{ fontSize: '0.92rem', fontWeight: 700, color: e.color, fontFamily: "'DM Sans', sans-serif", marginBottom: '0.4rem' }}>{e.result}</p>
                <p style={{ fontSize: '0.88rem', color: '#64748b', lineHeight: 1.65, fontFamily: "'DM Sans', sans-serif", margin: 0 }}>{e.note}</p>
              </div>
            ))}
          </div>

          {/* The Decision — dark bg box */}
          <div style={{ background: '#0a0e1a', borderRadius: '12px', padding: '1.75rem 2rem', marginBottom: '1.5rem' }}>
            <p style={{ fontSize: '0.72rem', fontFamily: "'DM Mono', monospace", color: accent, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.75rem', fontWeight: 600 }}>The Decision That Almost Did Not Happen</p>
            <p style={{ fontSize: '1.02rem', fontWeight: 700, color: '#fff', fontFamily: "'DM Sans', sans-serif", lineHeight: 1.5, marginBottom: '1rem' }}>
              The safety team wanted to add a mandatory confirmation step before every drone flight — a full checklist screen that engineers had to complete before launching.
            </p>
            <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.78)', lineHeight: 1.8, fontFamily: "'DM Sans', sans-serif", marginBottom: '0.85rem' }}>
              Their concern was completely valid from a compliance perspective. But testing showed that a multi-step pre-flight screen at height, in wind, with gloves, took over 4 minutes and introduced more errors than it prevented — engineers rushed through it to get it done.
            </p>
            <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.78)', lineHeight: 1.8, fontFamily: "'DM Sans', sans-serif", marginBottom: '0.85rem' }}>
              The solution: convert the checklist into a single summary screen with large visual indicators, completable in under 60 seconds. Safety team approved it after seeing the error-rate comparison data from both versions.
            </p>
            <div style={{ background: `rgba(${accentR},0.08)`, border: `1px solid rgba(${accentR},0.2)`, borderRadius: '8px', padding: '1rem 1.25rem' }}>
              <p style={{ fontSize: '0.88rem', color: accent, lineHeight: 1.7, fontFamily: "'DM Sans', sans-serif", margin: 0, fontStyle: 'italic' }}>
                Data, not opinion, resolved the disagreement. We made it a rule: every internal design debate would be settled by testing, not seniority.
              </p>
            </div>
          </div>

          {/* Documented Design Principle */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '1.25rem 1.5rem', display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
            <span style={{ fontSize: '1.4rem', lineHeight: 1, flexShrink: 0, marginTop: '2px' }}><FlatIcon name="rule" size={22} color="#94a3b8" /></span>
            <div>
              <p style={{ fontSize: '0.72rem', fontFamily: "'DM Mono', monospace", color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '0.4rem', fontWeight: 600 }}>DOCUMENTED DESIGN PRINCIPLE</p>
              <p style={{ fontSize: '0.95rem', color: '#1A1A1A', lineHeight: 1.7, fontFamily: "'DM Sans', sans-serif", margin: 0, fontStyle: 'italic' }}>
                "Design for the worst possible conditions first. If it works in direct sunlight with gloves at 50 metres, it will work anywhere."
              </p>
              <p style={{ fontSize: '0.78rem', color: '#94a3b8', fontFamily: "'DM Sans', sans-serif", margin: '0.5rem 0 0' }}>
                This became the baseline for every usability test conducted on the platform.
              </p>
            </div>
          </div>
        </div>
      </Accordion>

      {/* ── Business Impact ─────────────────────────────────────────── */}
      <Accordion label="📈 Business Impact" accent={accent}>
        <div style={T.section} className="cs-section">
          <h3 style={T.sectionTitle}>Measurable Results</h3>
          <div style={T.impactGroups}>
            {IMPACT_GROUPS.map((group) => (
              <div key={group.groupLabel} style={T.impactGroup}>
                <div style={{...T.impactGroupLabel, display:"flex", alignItems:"center", gap:"0.5rem"}}><FlatIcon name={group.groupIcon || "bolt"} size={14} color="currentColor" />{group.groupLabel}</div>
                <div style={T.impactStats} className="cs-impact-stats">
                  {group.stats.map((s) => (
                    <div key={s.desc} style={T.impactStat}>
                      <span style={{ ...T.impactNum, color: accent }}>{s.num}</span>
                      <span style={T.impactDesc}>
                        {s.desc}
                        {s.sub && <small style={{ display: 'block', fontSize: '0.68rem', color: '#94a3b8', marginTop: '2px' }}>{s.sub}</small>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Accordion>

      {/* ── User Feedback ───────────────────────────────────────────── */}
      <Accordion label="💬 User Feedback" accent={accent}>
        <div style={T.section} className="cs-section">
          <h3 style={T.sectionTitle}>In Their Own Words</h3>
          <div style={T.quotesGrid} className="cs-quotes-grid">
            {[
              'I can actually read this screen in the field. The big buttons work with my gloves. This is the first tool that was clearly built for people like me.',
              'The AI used to show me a wall of numbers I did not understand. Now it just tells me what is wrong and how serious it is. I trust it now.',
              'I used to spend 4 hours writing reports after a full day in the field. Now I tap one button and it is done before I get back to the vehicle.',
            ].map((q, i) => (
              <div key={i} style={T.userQuote}>{q}</div>
            ))}
          </div>
        </div>
      </Accordion>

      {/* ── Key Learnings ───────────────────────────────────────────── */}
      <Accordion label="💡 Key Learnings &amp; Reflection" accent={accent}>
        <div style={{ ...T.section, background: '#f8fafc' }} className="cs-section">
          <h3 style={T.sectionTitle}>What We Learned</h3>
          <div style={T.learningsGrid} className="cs-learnings-grid">
            <div style={T.learningsCol}>
              <h4 style={{ ...T.learningsColTitle, borderColor: accent }}>What Worked</h4>
              <ul style={T.caseList}>
                {[
                  'Site visits were the single most valuable research method — nothing in the office could replicate the reality of field conditions',
                  'Simulated outdoor usability testing (sunlight, gloves, time pressure) caught critical issues that lab testing completely missed',
                  'Plain-language AI explanations over technical confidence scores — engineers acted on recommendations they could understand',
                  'Two-mode design (Field Mode vs. Desktop Mode) resolved the tension between simplicity for engineers and depth for managers',
                ].map((item) => (
                  <li key={item} style={{...T.caseListItem, display:"flex", gap:"0.5rem", alignItems:"flex-start", paddingLeft:0, position:"static"}}><FlatIcon name="check_circle" size={14} color={accent} style={{flexShrink:0,marginTop:"5px"}} />{item}</li>
                ))}
              </ul>
            </div>
            <div style={T.learningsCol}>
              <h4 style={{ ...T.learningsColTitle, borderColor: accent }}>What I Would Change</h4>
              <ul style={T.caseList}>
                {[
                  'Involve safety officers earlier — their regulatory requirements shaped 30% of the final design and were discovered too late',
                  'Test offline sync edge cases from day one — data loss in the field is catastrophic and we found sync bugs only in late testing',
                  'Design the reporting template in parallel with the inspection flow — late changes to report format caused three rounds of rework',
                ].map((item) => (
                  <li key={item} style={{...T.caseListItem, display:"flex", gap:"0.5rem", alignItems:"flex-start", paddingLeft:0, position:"static"}}><FlatIcon name="trending_up" size={14} color="#94a3b8" style={{flexShrink:0,marginTop:"5px"}} />{item}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Core Takeaway */}
          <div style={T.takeaway}>
            <FlatIcon name="star" size={24} color={accent} style={T.takeawayIcon} />
            <div>
              <strong style={T.takeawayTitle}>Core Takeaway</strong>
              <p style={T.takeawayText}>The most important design research you can do is go where your users actually work. A desk-based designer cannot imagine the reality of using software at 50 metres in the wind. Getting into the field transformed every design decision and produced a product that engineers genuinely trusted with their safety.</p>
            </div>
          </div>
        </div>
      </Accordion>

    </div>
  );
}
